/**
 * Rename a declaration - both of its names.
 *
 * A SysML declaration carries two: the declared name, which is prose, and the
 * <angle-bracket> short name, which is the ID everything else refers to. They
 * are edited together here because renaming one and not the other is how a
 * model ends up with SUB_STR_2 called "Thermal margin", and because the API
 * takes both in a single PATCH - so it is one write either way.
 *
 * Changing an ID is not cosmetic: it is the address, and anything that referred
 * to the old one refers to nothing afterwards. That is said in the dialog rather
 * than discovered later.
 */

import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import { ApiError, api } from '../../api'
import type { ElementRow } from '../../schema'
import { MONOSPACE } from '../../theme'

export default function RenameElementDialog({
  row,
  onClose,
  onRenamed,
}: {
  row: ElementRow
  onClose: () => void
  onRenamed: (message: string) => void
}) {
  const [name, setName] = useState(row.name ?? '')
  const [identifier, setIdentifier] = useState(row.id ?? '')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const changed: Record<string, unknown> = {}
  if (name !== (row.name ?? '')) changed.name = name
  if (identifier !== (row.id ?? '')) changed.identifier = identifier

  const submit = async () => {
    setBusy(true)
    setFailure('')
    setFieldErrors({})
    try {
      await api.update(String(row.plural), row.id, changed)
      onRenamed(`${identifier || row.id} renamed`)
    } catch (error) {
      const failed = error as ApiError
      setFieldErrors(failed.fields ?? {})
      if (!Object.keys(failed.fields ?? {}).length) setFailure(failed.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rename {row.id || row.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}

          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name || 'The declared name, as it reads in the model'}
            size="small"
          />

          <TextField
            label="ID"
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            error={Boolean(fieldErrors.identifier)}
            helperText={
              fieldErrors.identifier ||
              'The short name in <angle brackets>. It is the address: anything referring to the old one will no longer resolve.'
            }
            size="small"
            slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={busy || Object.keys(changed).length === 0}
        >
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
